import React, { FC, SetStateAction } from 'react';
import { Button, ButtonSizes, ButtonStyles, Checkbox, Dropdown, Icons, Switcher } from '@silverfort/sf-components';
import SecurityControlItem from './SecurityControlItem';
import CustomControlItem from './CustomControlItem';
import { InsuranceRules } from '../../api/types/InsuranceRules';
import { CustomControl, OldPasswordPolicy, PolicyData } from '../../api/types/InsuranceReport';

interface Props {
    emailServicePolicyData: PolicyData;
    setEmailServicePolicyData: React.Dispatch<SetStateAction<PolicyData>>;
    vpnServicePolicyData: PolicyData;
    setVpnServicePolicyData: React.Dispatch<SetStateAction<PolicyData>>;
    oldPasswordPolicy: OldPasswordPolicy;
    setOldPasswordPolicy: React.Dispatch<SetStateAction<OldPasswordPolicy>>;
    isCompanyLogoUsed: boolean;
    setIsCompanyLogoUsed: React.Dispatch<SetStateAction<boolean>>;
    customControls: CustomControl[];
    setCustomControls: React.Dispatch<SetStateAction<CustomControl[]>>;
    oldPasswordOptions: Record<string, string>;
    insuranceRules: InsuranceRules;
}

const InsuranceReportContent: FC<Props> = ({
    emailServicePolicyData,
    setEmailServicePolicyData,
    vpnServicePolicyData,
    setVpnServicePolicyData,
    oldPasswordPolicy,
    setOldPasswordPolicy,
    isCompanyLogoUsed,
    setIsCompanyLogoUsed,
    customControls,
    setCustomControls,
    oldPasswordOptions,
    insuranceRules
}) => {
    return (
        <div className="w-full h-full flex flex-col justify-between gap-[24px]">
            <div className="w-full flex flex-col gap-[8px] self-center">
                <div className="text-13 text-black font-sf font-medium">Security Controls</div>
                <div className="w-full h-[160px] flex flex-col flex-1 gap-[8px] p-[16px] rounded-lg border">
                    <SecurityControlItem
                        policy={emailServicePolicyData}
                        setPolicy={setEmailServicePolicyData}
                        itemTitle="Email service policies"
                        selectPolicyComponent={
                            <Dropdown
                                variant="trigger"
                                items={insuranceRules.options?.map((rule) => ({ value: rule.name, title: rule.name }))}
                                selectedItems={emailServicePolicyData.policies}
                                onChange={(value) =>
                                    setEmailServicePolicyData({ selected: true, policies: (value as string[]) ?? null })
                                }
                                multiple
                                size="small"
                                text="Select policy"
                            />
                        }
                    />
                    <SecurityControlItem
                        policy={vpnServicePolicyData}
                        setPolicy={setVpnServicePolicyData}
                        itemTitle="VPN service policies"
                        selectPolicyComponent={
                            <Dropdown
                                variant="trigger"
                                items={insuranceRules.options?.map((rule) => ({ value: rule.name, title: rule.name }))}
                                selectedItems={vpnServicePolicyData.policies}
                                onChange={(value) =>
                                    setVpnServicePolicyData({ selected: true, policies: (value as string[]) ?? null })
                                }
                                multiple
                                size="small"
                                text="Select policy"
                            />
                        }
                    />
                    <SecurityControlItem
                        policy={oldPasswordPolicy}
                        setPolicy={setOldPasswordPolicy}
                        itemTitle="Service accounts with passwords older than:"
                        selectPolicyComponent={
                            <div className="w-[236px] flex flex-1 bg-sf-light-100 rounded-lg">
                                {Object.entries(oldPasswordOptions).map(([optionKey, optionTitle]) => (
                                    <Switcher
                                        key={optionKey}
                                        checked={oldPasswordPolicy.value === optionKey}
                                        text={optionTitle}
                                        onClick={() => setOldPasswordPolicy({ selected: true, value: optionKey })}
                                        className="p-[2px]"
                                    />
                                ))}
                            </div>
                        }
                    />
                </div>
            </div>
            <div className="flex flex-col gap-[8px]">
                <div className="text-13 text-black font-sf font-medium">Custom Controls</div>
                <div className="w-full h-[152px] p-[16px] flex flex-col flex-1 gap-[16px] border rounded-lg">
                    <div className="w-full h-fit max-h-[80px] flex flex-col gap-[16px] overflow-y-scroll">
                        {customControls.map((control, index) => (
                            <CustomControlItem
                                key={index}
                                setCustomControls={setCustomControls}
                                customControl={control}
                                index={index}
                                insuranceRules={insuranceRules}
                            />
                        ))}
                    </div>
                    <Button
                        text="Add"
                        buttonSize={ButtonSizes.Small}
                        iconLeft={Icons.Add}
                        iconSize={16}
                        buttonStyle={ButtonStyles.Primary}
                        onClick={() => setCustomControls((prevState) => [{ name: '', policies: [] }, ...prevState])}
                    />
                </div>
            </div>
            <div className="flex flex-col gap-[8px]">
                <div className="text-13 text-black font-sf font-medium">Logo</div>
                <div className="w-full h-[32px] flex items-center gap-[8px]">
                    <div className="h-full min-w-[160px] flex items-center gap-[8px]">
                        <Checkbox
                            onChange={(e) => setIsCompanyLogoUsed(e.target.checked)}
                            checked={isCompanyLogoUsed}
                        />
                        <div className="text-13 text-sf-gray-800 font-sf">Use company logo from settings</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InsuranceReportContent;
